import { MarketNightlyRefreshOverrideIntent, MarketNightlyRefreshProviderSet, MarketProviderSetStatus, ProviderSetBadge, ProviderSetStagedMap, ProviderSetStatusFilter } from "@app/operations/market-nightly-refresh/interfaces"
import { formatDate, formatDuration, compareWithNullsLast, toCsvString, formatDateFile } from "@app/operations/market-nightly-refresh/utils";
import { MarketNightlyRefreshService } from "@app/operations/market-nightly-refresh/marketNightlyRefreshService";
import { Waffles } from "@app/shared/waffles/Waffles";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { filterProviderSetsByPill, filterProviderSetsBySearch, countProviderSetsByFilter, ProviderSetFilterPills, isProviderSetIgnored, isProviderSetRunning } from "@app/operations/market-nightly-refresh/MarketNightlyRefreshFilters";
import { BadgeCell, CopyCell, StatusCell, YesNoCell } from "@app/operations/market-nightly-refresh/components/ProviderSetTableCells";
import { ProviderSetRowActionMenu } from "@app/operations/market-nightly-refresh/components/ProviderSetRowActionMenu";
import { PendingChangesBanner } from "@app/operations/market-nightly-refresh/components/PendingChangesBanner";
import { RunNowPrompt } from "@app/operations/market-nightly-refresh/components/RunNowPrompt";
import { RunReviewBanner } from "@app/operations/market-nightly-refresh/components/RunReviewBanner";
import { TableActions } from "@app/operations/market-nightly-refresh/components/TableActions";
import { useDebouncedValue } from "@app/shared/hooks/useDebouncedValue";
import { UserContext } from "@app/user/UserContext";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useState, useMemo, useCallback, useContext, useRef, FC } from "react";

interface ProviderSetTableWaffleToppings {
  key: string;
  name: React.ReactNode;
  providerSetId: React.ReactNode;
  providerSetVersionId: React.ReactNode;
  status: React.ReactNode;
  usableResults: React.ReactNode;
  lastRunDate: string;
  runtimeSeconds: string;
  systemData: React.ReactNode;
  projectsUsing: number;
  uniqueProviders: string;
  uniqueLocations: string;
  totalAccessPoints: string;
  actions: React.ReactNode;
  // not in columnConfig — used by rowDecorator only
  isEffectivelyIgnored: boolean;
  // hidden sort keys
  nameSortKey: string;
  providerSetIdSortKey: number;
  providerSetVersionIdSortKey: number;
  statusSortKey: MarketProviderSetStatus | null;
  usableResultsSortKey: boolean;
  lastRunDateSortKey: string | null;
  runtimeSecondsSortKey: number | null;
  systemDataSortKey: boolean;
  uniqueProvidersSortKey: number | null;
  uniqueLocationsSortKey: number | null;
  totalAccessPointsSortKey: number | null;
}

const getProviderSetBadge = (providerSet: MarketNightlyRefreshProviderSet, stagedShouldBeIgnored: boolean | undefined): ProviderSetBadge | null => {
  if (stagedShouldBeIgnored !== undefined) {
    return stagedShouldBeIgnored ? "ignore-pending" : "remove-ignore-pending";
  }
  if (providerSet.overrideType === "Exclude") return "excluded"
  if (providerSet.overrideType === "Include") return "included"
  if (providerSet.isLowUsage) return "system-ignored";
  return null;
}

const isEffectivelyIgnored = (providerSet: MarketNightlyRefreshProviderSet, stagedShouldBeIgnored: boolean | undefined): boolean =>
  stagedShouldBeIgnored !== undefined ? stagedShouldBeIgnored : isProviderSetIgnored(providerSet);

const overrideToIncludeSets = (
  intents: MarketNightlyRefreshOverrideIntent[],
  providersById: Map<string, MarketNightlyRefreshProviderSet>
): MarketNightlyRefreshProviderSet[] =>
  intents
    .filter((intent) => !intent.shouldBeIgnored)
    .map((intent) => providersById.get(String(intent.providerSetId)))
    .filter((p): p is MarketNightlyRefreshProviderSet => !!p && p.isLowUsage);

const buildProviderSetTableWaffleToppings = (
  providers: MarketNightlyRefreshProviderSet[],
  stagedChanges: ProviderSetStagedMap,
  onToggleStage: (provider: MarketNightlyRefreshProviderSet) => void
): ProviderSetTableWaffleToppings[] => {
  return providers.map((p) => {
    const stagedShouldBeIgnored = stagedChanges.get(p.providerSetId);
    const effectivelyIgnored = isEffectivelyIgnored(p, stagedShouldBeIgnored);
    /* oxlint-disable sort-keys */
    return {
      [wafflesKeyColumnName]: String(p.providerSetId),
      name: (
        <span className="market-nightly-refresh__name-cell">
          {effectivelyIgnored ? <span>{p.name}</span> : <strong>{p.name}</strong>}
          <BadgeCell badge={getProviderSetBadge(p, stagedShouldBeIgnored)} />
        </span>
      ),
      nameSortKey: p.name,
      providerSetId: <CopyCell label="Provider Set ID" value={p.providerSetId} />,
      providerSetIdSortKey: p.providerSetId,
      providerSetVersionId: <CopyCell label="Version ID" value={p.providerSetVersionId} />,
      providerSetVersionIdSortKey: p.providerSetVersionId,
      status: <StatusCell status={p.status} />,
      statusSortKey: p.status,
      usableResults: <YesNoCell value={p.usableResults} />,
      usableResultsSortKey: p.usableResults,
      lastRunDate: p.lastRunDate ? formatDate(p.lastRunDate) : "-",
      lastRunDateSortKey: p.lastRunDate,
      runtimeSeconds: p.runtimeSeconds != null ? formatDuration(p.runtimeSeconds) : "-",
      runtimeSecondsSortKey: p.runtimeSeconds,
      systemData: <YesNoCell value={p.isSystemData} />,
      systemDataSortKey: p.isSystemData,
      projectsUsing: p.projectsUsing,
      uniqueProviders: p.uniqueProviders != null ? p.uniqueProviders.toLocaleString() : "-",
      uniqueProvidersSortKey: p.uniqueProviders,
      uniqueLocations: p.uniqueLocations != null ? p.uniqueLocations.toLocaleString() : "-",
      uniqueLocationsSortKey: p.uniqueLocations,
      totalAccessPoints: p.totalAccessPoints != null ? p.totalAccessPoints.toLocaleString() : "-",
      totalAccessPointsSortKey: p.totalAccessPoints,
      actions: <ProviderSetRowActionMenu provider={p} onToggleStage={onToggleStage} />,
      isEffectivelyIgnored: effectivelyIgnored,
    };
    /* oxlint-enable sort-keys */
  })
}

/* oxlint-disable sort-keys */
const columnConfig: ColumnConfig = {
  name: { displayText: "Name", sticky: true, testId: "mnr-table__name", sizeContentToFit: true },
  providerSetId: { displayText: "Provider Set ID", sizeContentToFit: true, testId: "mnr-table__provider-set-id" },
  providerSetVersionId: { displayText: "Version ID", sizeContentToFit: true, testId: "mnr-table__version-id" },
  status: { displayText: "Status", sizeContentToFit: true, testId: "mnr-table__status" },
  usableResults: {
    displayText: "Usable Results",
    sizeContentToFit: true,
    testId: "mnr-table__usable-results",
    tooltip: "Identifies when a market provider set version has a completed result within 36 hours that can be used by a project run."
  },
  lastRunDate: { displayText: "Last Run Date", sizeContentToFit: true, testId: "mnr-table__last-run-date" },
  runtimeSeconds: { displayText: "Runtime", sizeContentToFit: true, testId: "mnr-table__runtime" },
  systemData: {
    displayText: "System Data",
    sizeContentToFit: true,
    testId: "mnr-table__system-data",
    tooltip: "Identifies whether a market provider set is System Data (client 0) or client specific."
  },
  projectsUsing: { displayText: "Projects Using", sizeContentToFit: true, testId: "mnr-table__projects-using" },
  uniqueProviders: { displayText: "Total Unique Providers", sizeContentToFit: true, testId: "mnr-table__total-unique-providers" },
  uniqueLocations: { displayText: "Total Unique Locations", sizeContentToFit: true, testId: "mnr-table__total-unique-locations" },
  totalAccessPoints: { displayText: "Total Unique Access Points", sizeContentToFit: true, testId: "mnr-table__total-unique-access-points" },
  actions: {
    displayText: "",
    noHorizontalPadding: true,
    noResize: true,
    noSort: true,
    sizeContentToFit: true,
    testId: "mnr-table__actions",
  },
};
/* oxlint-enable sort-keys */

interface CsvColumn {
  header: string;
  value: (row: ProviderSetTableWaffleToppings) => string;
}

const CSV_COLUMNS: CsvColumn[] = [
  { header: "Name", value: (r) => r.nameSortKey },
  { header: "Provider Set ID", value: (r) => String(r.providerSetIdSortKey) },
  { header: "Version ID", value: (r) => String(r.providerSetVersionIdSortKey) },
  { header: "Status", value: (r) => r.statusSortKey ?? "" },
  { header: "Usable Results", value: (r) => (r.usableResultsSortKey ? "Yes" : "No") },
  { header: "Last Run Date", value: (r) => (r.lastRunDateSortKey ? formatDate(r.lastRunDateSortKey) : "") },
  { header: "Runtime", value: (r) => (r.runtimeSecondsSortKey != null ? formatDuration(r.runtimeSecondsSortKey) : "") },
  { header: "System Data", value: (r) => (r.systemDataSortKey ? "Yes" : "No") },
  { header: "Projects Using", value: (r) => String(r.projectsUsing) },
  { header: "Total Unique Providers", value: (r) => (r.uniqueProvidersSortKey != null ? String(r.uniqueProvidersSortKey) : "") },
  { header: "Total Unique Locations", value: (r) => (r.uniqueLocationsSortKey != null ? String(r.uniqueLocationsSortKey) : "") },
  { header: "Total Unique Access Points", value: (r) => (r.totalAccessPointsSortKey != null ? String(r.totalAccessPointsSortKey) : "") },
];

const buildProviderSetsCsv = (rows: ProviderSetTableWaffleToppings[]): string =>
  toCsvString(
    CSV_COLUMNS.map((c) => c.header),
    rows.map((r) => CSV_COLUMNS.map((c) => c.value(r)))
  );

const rowDecorator = (row: ProviderSetTableWaffleToppings): string => {
  if (row.isEffectivelyIgnored) return "market-nightly-refresh__row--ignored";
  return "";
};

const handleProviderSetTableSort = (
  segment: ProviderSetTableWaffleToppings[],
  column: keyof ProviderSetTableWaffleToppings,
  direction: SortDirections,
): ProviderSetTableWaffleToppings[] => {
  if (direction === SortDirections.None) return segment;
  const multiplier = direction === SortDirections.Asc ? 1 : -1;

  switch (column) {
    case "name":
      return [...segment].sort((a, b) => a.nameSortKey.toLowerCase().localeCompare(b.nameSortKey.toLowerCase()) * multiplier);
    case "providerSetId":
      return [...segment].sort((a, b) => (a.providerSetIdSortKey - b.providerSetIdSortKey) * multiplier);
    case "providerSetVersionId":
      return [...segment].sort((a, b) => (a.providerSetVersionIdSortKey - b.providerSetVersionIdSortKey) * multiplier);
    case "status":
      return [...segment].sort((a, b) => compareWithNullsLast(a.statusSortKey, b.statusSortKey, multiplier));
    case "usableResults":
      return [...segment].sort((a, b) => compareWithNullsLast(a.usableResultsSortKey, b.usableResultsSortKey, multiplier));
    case "lastRunDate":
      return [...segment].sort((a, b) => compareWithNullsLast(a.lastRunDateSortKey, b.lastRunDateSortKey, multiplier));
    case "runtimeSeconds":
      return [...segment].sort((a, b) => compareWithNullsLast(a.runtimeSecondsSortKey, b.runtimeSecondsSortKey, multiplier));
    case "systemData":
      return [...segment].sort((a, b) => compareWithNullsLast(a.systemDataSortKey, b.systemDataSortKey, multiplier));
    case "projectsUsing":
      return [...segment].sort((a, b) => (a.projectsUsing - b.projectsUsing) * multiplier);
    case "uniqueProviders":
      return [...segment].sort((a, b) => compareWithNullsLast(a.uniqueProvidersSortKey, b.uniqueProvidersSortKey, multiplier));
    case "uniqueLocations":
      return [...segment].sort((a, b) => compareWithNullsLast(a.uniqueLocationsSortKey, b.uniqueLocationsSortKey, multiplier));
    case "totalAccessPoints":
      return [...segment].sort((a, b) => compareWithNullsLast(a.totalAccessPointsSortKey, b.totalAccessPointsSortKey, multiplier));
    default:
      return segment;
  }
};

interface ProviderSetTableProps {
  isLoading: boolean;
  providers: MarketNightlyRefreshProviderSet[];
}

export const ProviderSetTable: FC<ProviderSetTableProps> = ({ isLoading, providers }) => {
  const [activeFilter, setActiveFilter] = useState<ProviderSetStatusFilter>("all");
  const [searchInput, setSearchInput] = useState("");
  const [stagedChanges, setStagedChanges] = useState<ProviderSetStagedMap>(new Map());
  const [runReviewCandidateIds, setRunReviewCandidateIds] = useState<number[]>([]);
  const [runQueue, setRunQueue] = useState<MarketNightlyRefreshProviderSet[]>([]);
  const debouncedSearch = useDebouncedValue(searchInput, 200);
  const service = useRef(new MarketNightlyRefreshService()).current;
  const queryClient = useQueryClient();
  const userContext = useContext(UserContext);
  const isOperationsAdmin = userContext.roles.includes(Roles.OPERATIONS_ADMIN);

  const applyOverridesMutation = useMutation({
    mutationFn: (intents: MarketNightlyRefreshOverrideIntent[]) => service.applyProviderSetOverrides(intents),
    onError: () => toast.error("Failed to apply pending changes. Please try again.", { containerId: "z-index-9999" }),
    onSuccess: (_data, variables) => {
      toast.success(`Applied ${variables.length} ${variables.length === 1 ? 'change' : 'changes'}`, { containerId: "z-index-9999" })
      setRunReviewCandidateIds(overrideToIncludeSets(variables, providersById).map((p) => p.providerSetId));
      setStagedChanges(new Map());
      queryClient.invalidateQueries({ queryKey: ["market-nightly-refresh-provider-sets"] });
    },
  });

  const handleApply = (): void => {
    const intents: MarketNightlyRefreshOverrideIntent[] = [...stagedChanges.entries()].map(([providerSetId, shouldBeIgnored]) => ({
      providerSetId,
      shouldBeIgnored,
    }));
    applyOverridesMutation.mutate(intents);
  };
  const handleCancel = (): void => setStagedChanges(new Map());

  const advanceRunQueue = useCallback(() => setRunQueue((prev) => prev.slice(1)), []);

  const runMutation = useMutation({
    mutationFn: (providerSet: MarketNightlyRefreshProviderSet) =>
      service.runProviderSetAnalysis(providerSet.providerSetId, providerSet.providerSetVersionId),
    onError: () => toast.error("Failed to start the run. Please try again.", { containerId: "z-index-9999" }),
    onSettled: advanceRunQueue,
    onSuccess: () => {
      toast.success("Run started.", { containerId: "z-index-9999" });
      queryClient.invalidateQueries({ queryKey: ["market-nightly-refresh-provider-sets"] });
    },
  });

  const searchFilteredProviders = useMemo(
    () => filterProviderSetsBySearch({ providers, search: debouncedSearch }),
    [providers, debouncedSearch]
  );
  const filteredProviders = useMemo(
    () => filterProviderSetsByPill({ activeFilter, providers: searchFilteredProviders }),
    [activeFilter, searchFilteredProviders]
  );

  const counts = useMemo(() => countProviderSetsByFilter(searchFilteredProviders), [searchFilteredProviders]);

  const toggleStagedChange = useCallback((provider: MarketNightlyRefreshProviderSet) => {
    setStagedChanges((prev) => {
      const next = new Map(prev);
      if (next.has(provider.providerSetId)) {
        next.delete(provider.providerSetId);
      } else {
        next.set(provider.providerSetId, !isProviderSetIgnored(provider));
      }
      return next;
    });
  }, []);

  const providersById = useMemo(
    () => new Map(providers.map((p) => [String(p.providerSetId), p])),
    [providers]
  )

  const runReviewCandidates = useMemo(
    () => runReviewCandidateIds
      .map((id) => providersById.get(String(id)))
      .filter((p): p is MarketNightlyRefreshProviderSet => !!p),
    [runReviewCandidateIds, providersById]
  );
  const runningReviewSets = useMemo(() => runReviewCandidates.filter(isProviderSetRunning), [runReviewCandidates]);
  const eligibleReviewSets = useMemo(() => runReviewCandidates.filter((p) => !isProviderSetRunning(p)), [runReviewCandidates]);

  const handleReviewRunQueue = useCallback((): void => {
    setRunQueue([...runningReviewSets, ...eligibleReviewSets]);
    setRunReviewCandidateIds([]);
  }, [runningReviewSets, eligibleReviewSets]);

  const handleCheckboxChange = useCallback((selectedKeys: string[]) => {
    const currentKeys = new Set([...stagedChanges.keys()].map(String));
    const newKeys = new Set(selectedKeys);

    const changedKey =
      selectedKeys.find((k) => !currentKeys.has(k)) ??
      [...currentKeys].find((k) => !newKeys.has(k));

    const provider = changedKey && providersById.get(changedKey);
    if (provider) toggleStagedChange(provider);
  }, [stagedChanges, providersById, toggleStagedChange]);

  const { toExcludeCount, toIncludeCount } = useMemo(() => {
    let toExcludeCount = 0;
    let toIncludeCount = 0;
    for (const shouldBeIgnored of stagedChanges.values()) {
      shouldBeIgnored ? toExcludeCount++ : toIncludeCount++;
    }
    return { toExcludeCount, toIncludeCount };
  }, [stagedChanges]);

  const tableData = useMemo(
    () => buildProviderSetTableWaffleToppings(filteredProviders, stagedChanges, toggleStagedChange),
    [filteredProviders, stagedChanges, toggleStagedChange]
  );

  const activeSortRef = useRef<{ column: keyof ProviderSetTableWaffleToppings; direction: SortDirections }>({
    column: "projectsUsing",
    direction: SortDirections.Desc,
  });

  const handleSort = useCallback(
    (
      segment: ProviderSetTableWaffleToppings[],
      column: keyof ProviderSetTableWaffleToppings,
      direction: SortDirections
    ): ProviderSetTableWaffleToppings[] => {
      activeSortRef.current = { column, direction };
      return handleProviderSetTableSort(segment, column, direction)
    }, []);

  const handleDownload = useCallback((): void => {
    const { column, direction } = activeSortRef.current;
    const sortedRows = handleProviderSetTableSort(tableData, column, direction);
    const csv = buildProviderSetsCsv(sortedRows);

    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `MarketNightlyRefresh_${formatDateFile(new Date())}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, [tableData]);

  return (
    <div className="market-nightly-refresh__table">
      <RoleRestricted allowedRoles={[Roles.OPERATIONS_ADMIN]}>
        <PendingChangesBanner
          changes={stagedChanges}
          toExclude={toExcludeCount}
          toInclude={toIncludeCount}
          onApply={handleApply}
          onCancel={handleCancel}
          isApplying={applyOverridesMutation.isPending}
        />
        <RunReviewBanner
          eligibleCount={eligibleReviewSets.length}
          runningCount={runningReviewSets.length}
          onReview={handleReviewRunQueue}
        />
        {runQueue[0] && (
          <RunNowPrompt
            key={runQueue[0].providerSetId}
            isRunning={runMutation.isPending}
            onNotNow={advanceRunQueue}
            onRunNow={() => runMutation.mutate(runQueue[0])}
            providerSet={runQueue[0]}
          />
        )}
      </RoleRestricted>
      <div className="market-nightly-refresh__table-controls" >
        <QESTextInput
          testId="mnr-table__search"
          label="Search"
          inputAppend={<QesIcon iconName={QesIcons.SearchSm} description="Search" />}
          placeholder="Search by Name, Provider Set ID, or Version ID"
          value={searchInput}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
          containerClassName="market-nightly-refresh__search"
        />
        <ProviderSetFilterPills
          activeFilter={activeFilter}
          counts={counts}
          onFilterChange={setActiveFilter}
        />
        <TableActions onDownload={handleDownload} />
      </div>
      <Waffles<ProviderSetTableWaffleToppings>
        columns={columnConfig}
        customSort={handleSort}
        data={tableData}
        loading={isLoading}
        rowDecorator={rowDecorator}
        selectedKeys={[...stagedChanges.keys()].map(String)}
        showCheckboxes={isOperationsAdmin}
        sortDirectionOverride={SortDirections.Desc}
        sortedColumnOverride="projectsUsing"
        handleCheckboxChange={handleCheckboxChange}
        hideBulkCheckbox={true}
        disableProcessingOverlay={true}
        variant="frozen-border"
        testId="market-nightly-refresh__table--all-providers"
      />
    </div>
  )
}